
/**
 * Core class representing the Aggregated Coordinate System Space (ACS) and the Model Coordinate System Space,
 * according to v3 schema definition.
 * Reference: https://git.autodesk.com/BIM360/visualization-schemas/blob/35c5171474298e2c2804b1aa68f3868926ba5bc6/autodesk.viewer_state-3.0.0.yaml#L8
 *
 * @memberof Autodesk.Viewing
 * @alias Autodesk.Viewing.CoordinateSystem
 */
export class CoordinateSystem {

  #tfAggregated = null;
  #tfSingleModel = null;
  #model;
  #type = 'Model'; // can be 'Model' or 'Shared' according to v3 schema definition

  constructor(model, type = 'Model') {
    this.#model = model;
    this.#type = type;
  }

  /**
   * Performs in-place transformation of the given camera properties into the target Coordinate Space,
   * defined by this.#type.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  transformCameraToCS(position, target, up, worldUp, pivot) {
    if (this.#type === 'Model') {
      this.#transformCameraToModelCS(position, target, up, worldUp, pivot);
      return;
    }

    if (this.#type === 'Shared') {
      this.#transformCameraToAggregatedCS(position, target, up, worldUp, pivot);
      return;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Performs in-place transformation of the given camera properties from the target Coordinate Space,
   * defined by this.#type.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  transformCameraFromCS(position, target, up, worldUp, pivot) {
    if (this.#type === 'Model') {
      this.#transformCameraFromModelCS(position, target, up, worldUp, pivot);
      return;
    }

    if (this.#type === 'Shared') {
      this.#transformCameraFromAggregatedCS(position, target, up, worldUp, pivot);
      return;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Performs in-place transformation of the given section planes to the target Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  transformSectionPlanesToCS(sectionPlanes) {
    if (this.#type === 'Model') {
      this.#transformSectionPlanesToModelCS(sectionPlanes);
      return;
    }

    if (this.#type === 'Shared') {
      this.#transformSectionPlanesToAggregatedCS(sectionPlanes);
      return;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Performs in-place transformation of the given section planes from target Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  transformSectionPlanesFromCS(sectionPlanes) {
    if (this.#type === 'Model') {
      this.#transformSectionPlanesFromModelCS(sectionPlanes);
      return;
    }

    if (this.#type === 'Shared') {
      this.#transformSectionPlanesFromAggregatedCS(sectionPlanes);
      return;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Transforms the orthographic height (in case of ortho camera) to target Coordinate Space.
   *
   * @param {Number} orthographicHeight
   * @returns {Number}
   */
  transformOrthographicHeightToCS(orthographicHeight) {
    if (this.#type === 'Model') {
      return orthographicHeight;
    }

    if (this.#type === 'Shared') {
      this.#transformOrthographicHeightToAggregatedCS(orthographicHeight);
      return;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Transforms the orthographic height (in case of ortho camera) from target Coordinate Space.
   *
   * @param {Number} orthographicHeight
   * @returns {Number}
   */
  transformOrthographicHeightFromCS(orthographicHeight) {
    if (this.#type === 'Model') {
      return orthographicHeight;
    }

    if (this.#type === 'Shared') {
      this.#transformOrthographicHeightFromAggregatedCS(orthographicHeight);
      return orthographicHeight;
    }

    // not supported type
    throw new Error('V3 Schema compliant ViewerState requires the Coordinate System Type to either "Model" or "Shared".');
  }

  /**
   * Performs in-place transformation of the given camera properties into Model Coordinate Space.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  #transformCameraToModelCS(position, target, up, worldUp, pivot) {
    const tf = this.#getTransformModelContext();
    this.#transformCamera(position, target, up, worldUp, pivot, tf);
  }

  /**
   * Performs in-place transformation of the given camera properties from Model Coordinate Space.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  #transformCameraFromModelCS(position, target, up, worldUp, pivot) {
    const tf = this.#getTransformModelContext().clone().invert();
    this.#transformCamera(position, target, up, worldUp, pivot, tf);
  }

  /**
   * Performs in-place transformation of the given camera properties into Aggregated Coordinate Space.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  #transformCameraToAggregatedCS(position, target, up, worldUp, pivot) {
    const tf = this.#getTransformAggregatedContext();
    this.#transformCamera(position, target, up, worldUp, pivot, tf);
  }

  /**
   * Performs in-place transformation of the given camera properties from Aggregated Coordinate Space.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   */
  #transformCameraFromAggregatedCS(position, target, up, worldUp, pivot) {
    const tf = this.#getTransformAggregatedContext().clone().invert();
    this.#transformCamera(position, target, up, worldUp, pivot, tf);
  }

  /**
   * Performs in-place transformation of the given section planes to Aggregated Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  #transformSectionPlanesToAggregatedCS(sectionPlanes) {
    const unitScale = this.#getUnitScale();
    const offset = this.#getGlobalOffset();
    const n = new THREE.Vector3();

    sectionPlanes.forEach((plane) => {
      n.set(plane.x, plane.y, plane.z);
      const d = (plane.w - n.dot(offset)) * unitScale;
      plane.set(n.x, n.y, n.z, d);
    });
  }

  /**
   * Performs in-place transformation of the given section planes from Aggregated Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  #transformSectionPlanesFromAggregatedCS(sectionPlanes) {
    const unitScale = this.#getUnitScale();
    const offset = this.#getGlobalOffset();
    const n = new THREE.Vector3();

    sectionPlanes.forEach((plane) => {
      n.set(plane.x, plane.y, plane.z);
      const d = plane.w / unitScale + n.dot(offset);
      plane.set(n.x, n.y, n.z, d);
    });
  }

  /**
   * Performs in-place transformation of the given section planes to Model Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  #transformSectionPlanesToModelCS(sectionPlanes) {
    const tf = this.#getTransformModelContext();
    const planeTf = new THREE.Plane();

    sectionPlanes.forEach((plane) => {
      planeTf.setComponents(plane.x, plane.y, plane.z, plane.w);
      planeTf.applyMatrix4(tf);
      plane.set(planeTf.normal.x, planeTf.normal.y, planeTf.normal.z, planeTf.constant);
    });
  }

  /**
   * Performs in-place transformation of the given section planes from Model Coordinate Space.
   *
   * @param {Array<Vector4>} sectionPlanes - section planes
   */
  #transformSectionPlanesFromModelCS(sectionPlanes) {
    const tf = this.#getTransformModelContext().clone().invert();
    const planeTf = new THREE.Plane();

    sectionPlanes.forEach((plane) => {
      planeTf.setComponents(plane.x, plane.y, plane.z, plane.w);
      planeTf.applyMatrix4(tf);
      plane.set(planeTf.normal.x, planeTf.normal.y, planeTf.normal.z, planeTf.constant);
    });
  }

  /**
   * Transforms the orthographic height (in case of ortho camera) to Aggregated Coordinate Space.
   *
   * @param {Number} orthographicHeight
   * @returns {Number}
   */
  #transformOrthographicHeightToAggregatedCS(orthographicHeight) {
    const unitScale = this.#getUnitScale();
    return orthographicHeight * unitScale;
  }

  /**
   * Transforms the orthographic height (in case of ortho camera) from Aggregated Coordinate Space.
   *
   * @param {Number} orthographicHeight
   * @returns {Number}
   */
  #transformOrthographicHeightFromAggregatedCS(orthographicHeight) {
    const unitScale = this.#getUnitScale();
    return orthographicHeight / unitScale;
  }

  /**
   * Performs in-place transformation of the given camera properties by the given matrix.
   *
   * @param {THREE.Vector3} position - camera eye
   * @param {THREE.Vector3} target - camera target
   * @param {THREE.Vector3} up - camera up
   * @param {THREE.Vector3} worldUp - model up
   * @param {THREE.Vector3} pivot - pivot point
   * @param {THREE.Matrix4} tf - the transformation to apply
   */
  #transformCamera(position, target, up, worldUp, pivot, tf) {
    position.applyMatrix4(tf);
    target.applyMatrix4(tf);
    pivot.applyMatrix4(tf);

    const normalMatrix = new THREE.Matrix3();
    normalMatrix.getNormalMatrix(tf);
    up.applyMatrix3(normalMatrix);
    worldUp.applyMatrix3(normalMatrix);

    up.normalize();
    worldUp.normalize();
  }

  /**
   * Creates a transformation that transforms into Aggregated Coordinate Space.
   *
   * @returns {THREE.Matrix4}
   */
  #getTransformAggregatedContext() {
    this.#tfAggregated = new THREE.Matrix4().identity();

    const scaleFactor = this.#getUnitScale();
    const scaleToMeters = new THREE.Matrix4().makeScale(scaleFactor, scaleFactor, scaleFactor);
    this.#tfAggregated.multiply(scaleToMeters);

    const globalOffset = this.#getGlobalOffset();
    const globalOffsetTf = new THREE.Matrix4().makeTranslation(globalOffset.x, globalOffset.y, globalOffset.z);
    this.#tfAggregated.multiply(globalOffsetTf);

    return this.#tfAggregated;
  }

  /**
   * Creates a transformation that transforms into Model Coordinate Space.
   *
   * @returns {THREE.Matrix4}
   */
  #getTransformModelContext() {
    this.#tfSingleModel = new THREE.Matrix4().identity();

    const tf = this.#model.getInverseModelToViewerTransform();
    if (tf) {
      this.#tfSingleModel.multiply(tf);
    }

    return this.#tfSingleModel;
  }

  /**
   * Returns the common global offset.
   * If not possible, zero vector is returned.
   *
   * @returns {THREE.Vector3} - the global offset
   */
  #getGlobalOffset() {
    if (!this.#model) {
      return new THREE.Vector3();
    }

    const offset = this.#model.getGlobalOffset();
    if (!offset) {
      return new THREE.Vector3();
    }

    return offset;
  }

  /**
   * Returns the common unit scale.
   * If not possible to get, 1 is returned.
   *
   * @returns {Number} - unit scale
   */
  #getUnitScale() {
    if (!this.#model) {
      return 1;
    }

    return this.#model.getUnitScale();
  }
}